import React, { FC, useCallback, useEffect } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { SwapFormFields } from 'modules/payments/hooks/use-swap-form';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { CurrencyInput } from 'shared/components/Form/CurrencyInput';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { FORMATTED_PAIRS, PAIRS_TOGGLE } from 'modules/payments/constants/swap-pairs';
import { convertCrypto, convertReverseCrypto } from 'shared/utils';
import { uniq } from 'lodash';
import { EXCHANGE_RATE_NAME_TO_CURRENCY } from '@okletsplay/core';

interface SwapFormProps {
  form: UseFormReturn<SwapFormFields>;
  isDisabled: boolean;
  exchangeRate: number | undefined;
}

export const SwapForm: FC<SwapFormProps> = ({ form, isDisabled, exchangeRate }) => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = form;

  const [pair, selectedSide] = watch(['pair', 'selectedSide']);
  const currencies = EXCHANGE_RATE_NAME_TO_CURRENCY[pair];

  const togglePair = () => {
    if (isDisabled) return;

    setValue('pair', PAIRS_TOGGLE[pair]);
    setValue('sell', 0, { shouldValidate: true });
    setValue('buy', 0, { shouldValidate: true });
  };

  const updateSellWithRate = useCallback(() => {
    setValue('sell', convertReverseCrypto(getValues().buy, exchangeRate ?? 0));
  }, [exchangeRate, getValues, setValue]);

  const updateBuyWithRate = useCallback(() => {
    setValue('buy', convertCrypto(getValues().sell, exchangeRate ?? 0));
  }, [exchangeRate, getValues, setValue]);

  useEffect(() => {
    if (selectedSide === 'sellingCurrency') {
      updateBuyWithRate();
    } else {
      updateSellWithRate();
    }
  }, [updateSellWithRate, updateBuyWithRate, exchangeRate, selectedSide]);

  return (
    <FormProvider {...form}>
      <Grid mb={1} container rowSpacing={1.5} columnSpacing={1}>
        <Grid item xs={2} display="flex" alignItems="center">
          <Typography>{currencies.sell}</Typography>
        </Grid>
        <Grid item xs={10}>
          <Controller
            control={control}
            name="sell"
            render={({ field }) => (
              <CurrencyInput
                {...field}
                disabled={isDisabled}
                suffix=""
                prefix={selectedSide === 'buyingCurrency' ? '≈ ' : ''}
                onFocus={() => setValue('selectedSide', 'sellingCurrency')}
                onBlur={updateBuyWithRate}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} display="flex" alignItems="center" justifyContent="center">
          <Button
            variant="contained"
            endIcon={<SwapVertIcon />}
            onClick={togglePair}
            disabled={isDisabled}
            style={{ textTransform: 'none' }}
          >
            {FORMATTED_PAIRS[pair]}
          </Button>
        </Grid>
        <Grid item xs={2} display="flex" alignItems="center">
          <Typography>{currencies.buy}</Typography>
        </Grid>
        <Grid item xs={10}>
          <Controller
            control={control}
            name="buy"
            render={({ field }) => (
              <CurrencyInput
                {...field}
                disabled={isDisabled}
                suffix=""
                prefix={selectedSide === 'sellingCurrency' ? '≈ ' : ''}
                onFocus={() => setValue('selectedSide', 'buyingCurrency')}
                onBlur={updateSellWithRate}
              />
            )}
          />
        </Grid>
      </Grid>
      <Box mb={1} display="flex">
        {(errors.buy?.message || errors.sell?.message) && (
          <Box mr={1}>
            <ErrorMessage
              error={uniq([errors.buy?.message, errors.sell?.message]).filter(Boolean).join('. ')}
              withSidePadding={false}
            />
          </Box>
        )}
      </Box>
    </FormProvider>
  );
};
